import { FbaPrepType, FbaTransportV2024ItemPrepDetail, ItemPrepDetailInput, OwnerConstraint, PrepCategory, PrepType } from '@conveyr/shared-types';
import { AxiosInstance } from 'axios';
import type { operations } from '@/api/generated/openapi';
import { axiosWithoutCaseMiddleware } from '@/helpers/forms/axios';

export type FetchPrepDetailsQuery = operations['fetch-fba-transport-v2024-item-prep-details']['parameters']['query'];
export type FetchPrepDetailsResponse = operations['fetch-fba-transport-v2024-item-prep-details']['responses']['200']['content']['application/json'];
export type SetPrepDetailsRequest = operations['set-fba-transport-v2024-item-prep-details']['requestBody']['content']['application/json'];
export type SetPrepDetailsResponse = operations['set-fba-transport-v2024-item-prep-details']['responses']['200']['content']['application/json'];
// The backend resource sends channel_id in snake_case and every other field in camelCase.
export type ItemPrepDetailRaw = FetchPrepDetailsResponse['data'][number];

const ownerConstraints: Record<NonNullable<ItemPrepDetailRaw['labelOwnerConstraint']>, OwnerConstraint> = {
    AMAZON_ONLY: OwnerConstraint.AmazonOnly,
    SELLER_ONLY: OwnerConstraint.SellerOnly,
    NONE_ONLY: OwnerConstraint.NoneOnly,
};

const prepCategories: Record<ItemPrepDetailRaw['prepCategory'], PrepCategory> = {
    ADULT: PrepCategory.Adult,
    BABY: PrepCategory.Baby,
    FC_PROVIDED: PrepCategory.FcProvided,
    FRAGILE: PrepCategory.Fragile,
    GRANULAR: PrepCategory.Granular,
    HANGER: PrepCategory.Hanger,
    LIQUID: PrepCategory.Liquid,
    PERFORATED: PrepCategory.Perforated,
    SET: PrepCategory.Set,
    SHARP: PrepCategory.Sharp,
    SMALL: PrepCategory.Small,
    TEXTILE: PrepCategory.Textile,
    UNKNOWN: PrepCategory.Unknown,
    NONE: PrepCategory.None,
};

const prepTypes: Record<ItemPrepDetailRaw['prepTypes'][number], FbaPrepType> = {
    ITEM_LABELING: PrepType.ItemLabeling,
    ITEM_BUBBLEWRAP: PrepType.ItemBubblewrap,
    ITEM_POLYBAGGING: PrepType.ItemPolybagging,
    ITEM_TAPING: PrepType.ItemTaping,
    ITEM_BLACK_SHRINKWRAP: PrepType.ItemBlackShrinkwrap,
    ITEM_HANG_GARMENT: PrepType.ItemHangGarment,
    ITEM_BOXING: PrepType.ItemBoxing,
    ITEM_SETCREAT: PrepType.ItemSetcreat,
    ITEM_RMOVHANG: PrepType.ItemRmovhang,
    ITEM_SUFFOSTK: PrepType.ItemSuffostk,
    ITEM_CAP_SEALING: PrepType.ItemCapSealing,
    ITEM_DEBUNDLE: PrepType.ItemDebundle,
    ITEM_SETSTK: PrepType.ItemSetstk,
    ITEM_SIOC: PrepType.ItemSioc,
    ITEM_NO_PREP: PrepType.ItemNoPrep,
    ADULT: PrepCategory.Adult,
    BABY: PrepCategory.Baby,
    FC_PROVIDED: PrepCategory.FcProvided,
    FRAGILE: PrepCategory.Fragile,
    GRANULAR: PrepCategory.Granular,
    HANGER: PrepCategory.Hanger,
    LIQUID: PrepCategory.Liquid,
    PERFORATED: PrepCategory.Perforated,
    SET: PrepCategory.Set,
    SHARP: PrepCategory.Sharp,
    SMALL: PrepCategory.Small,
    TEXTILE: PrepCategory.Textile,
    UNKNOWN: PrepCategory.Unknown,
    NONE: PrepCategory.None,
};

export function toItemPrepDetail(raw: ItemPrepDetailRaw): FbaTransportV2024ItemPrepDetail {
    return {
        channelId: raw.channel_id,
        msku: raw.msku,
        allOwnersConstraint: raw.allOwnersConstraint,
        labelOwnerConstraint: raw.labelOwnerConstraint === null ? null : ownerConstraints[raw.labelOwnerConstraint],
        prepOwnerConstraint: raw.prepOwnerConstraint === null ? null : ownerConstraints[raw.prepOwnerConstraint],
        prepCategory: prepCategories[raw.prepCategory],
        prepTypes: raw.prepTypes.map(prepType => prepTypes[prepType]),
    };
}

export default (axios: AxiosInstance = axiosWithoutCaseMiddleware): { fetchPrepDetails: (channelId: number, mskus: string[], fresh?: boolean) => Promise<FbaTransportV2024ItemPrepDetail[]>; setPrepDetails: (channelId: number, prepDetails: ItemPrepDetailInput[]) => Promise<string | undefined>; } => ({
    fetchPrepDetails: async (channelId: number, mskus: string[], fresh: boolean = false): Promise<FbaTransportV2024ItemPrepDetail[]> => {
        const query: FetchPrepDetailsQuery = { channel_id: channelId, mskus, fresh };
        // The backend has always been sent fresh as 1/0 rather than Axios's true/false.
        const response = await axios.get<FetchPrepDetailsResponse>('/api/fba-transport/v2024/item-prep-details', {
            params: { ...query, fresh: query.fresh === true ? '1' : '0' },
        });

        return response.data.data.map(toItemPrepDetail);
    },

    // A message response asks to retry without starting an operation, so it resolves to undefined and nothing is polled.
    setPrepDetails: async (channelId: number, prepDetails: ItemPrepDetailInput[]): Promise<string | undefined> => {
        const body: SetPrepDetailsRequest = {
            channel_id: channelId,
            prep_details: prepDetails.map(({ msku, prepCategory, prepTypes }) => ({ msku, prep_category: prepCategory, prep_types: prepTypes })),
        };
        const response = await axios.post<SetPrepDetailsResponse>('/api/fba-transport/v2024/item-prep-details', body);

        return 'operation_id' in response.data ? response.data.operation_id : undefined;
    },
});
